import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import {
  Alert,
  Button,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { Form, Formik, FormikHelpers } from 'formik'
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useSkipOnboadingStep } from '../../hooks/api/auth/useSkipOnboadingStep'
import { useUpdateOnboading } from '../../hooks/api/auth/useUpdateOnboading'
import { useGetCityById } from '../../hooks/api/cities/useGetCityById'
import { useAuthContext } from '../auth/hooks/useAuthContext'
import GenericDocs from '../components/documents/GenericDocs'
import LicenseDocs from '../components/documents/LicenseDocs'
import PersonalInfo from '../components/documents/PersonalInfo'
import ProfilePic from '../components/documents/ProfilePic'
import LanguageSelect from '../components/LanguageSelect'
import OnboardStepper from '../components/OnboardStepper'
import { StepDocuments } from '../components/StepDocuments'
import routes from '../router/routes'
import {
  FormSchemaValue,
  GenericInfoSchemaType,
  getGenericStepSchema,
  licenseInfoSchema,
  LicenseInfoSchemaType,
  personalInfoSchema,
  PersonalInfoSchemaType,
  profilePictureInfoSchema,
  ProfilePictureInfoSchemaType,
} from '../schema/input'
import { SnackBarAlertType } from '../types/interface'

const CONTACT_STEP_NAME = 'contact'
const PROFILE_PICTURE_STEP_NAME = 'profile_picture'
const DRIVER_STEP_NAME = 'driver_license'
const CANADA_ID = 1

const Onboard = () => {
  const navigate = useNavigate()
  const {
    getOnboardData,
    onboardUserData,
    setIsOnboarded,
    signOut,
  } = useAuthContext()
  const { t } = useTranslation()

  const { isPending: isUpdatingOnboarding, mutateAsync: updateOnboarding } =
    useUpdateOnboading()
  const { isPending: isSkippingOnboarding, mutateAsync: skipOnboardingStep } =
    useSkipOnboadingStep()

  const initialData = useMemo(
    () => onboardUserData?.data || [],
    [onboardUserData]
  )

  const initialStep = useMemo(
    () => onboardUserData?.sort || 0,
    [onboardUserData]
  )

  // Get city ID from contact step in initialData, then fetch city name
  const contactCityId = useMemo(() => {
    const contactData = initialData.find((d) => d.name === CONTACT_STEP_NAME)
    if (contactData?.city) {
      return typeof contactData.city === 'string'
        ? parseInt(contactData.city, 10)
        : contactData.city
    }
    return undefined
  }, [initialData])

  // Fetch city name by ID for onboarding restrictions
  const { data: city } = useGetCityById(contactCityId || 0, (data) => data.name)

  const [activeStep, setActiveStep] = useState(0)
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })

  useEffect(() => {
    const activeStepIndex = initialData.findIndex(
      (data) => data.sort === initialStep
    )

    if (activeStepIndex !== -1) {
      setActiveStep(activeStepIndex)
    }
  }, [initialData, initialStep])

  useEffect(() => {
    setAlertEvent((prevAlert) => ({ ...prevAlert, open: false }))
  }, [activeStep])

  const getSteps = useCallback(() => {
    const contactData = initialData.find(
      (data) => data.name === CONTACT_STEP_NAME
    )
    const profilePictureData = initialData.find(
      (data) => data.name === PROFILE_PICTURE_STEP_NAME
    )
    const driverLicenseData = initialData.find(
      (data) => data.name === DRIVER_STEP_NAME
    )

    const contactInitialValues = {
      address: contactData?.address || '',
      city: contactData?.city,
      country: contactData?.country_id ?? CANADA_ID,
      dateOfBirth: contactData?.birth_date || '',
      firstName: contactData?.first_name || '',
      lastName: contactData?.last_name || '',
      phoneNumber: contactData?.phone || '',
      required: contactData?.is_mandatory,
    } as const

    const driverLicenseInitialValues = {
      back: driverLicenseData?.key2 || '',
      city: contactData?.city,
      countryId: contactData?.country_id,
      docExpirationDate: driverLicenseData?.expiration_date || '',
      front: driverLicenseData?.key1 || '',
      licenseNumber: driverLicenseData?.license_number || '',
      required: driverLicenseData?.is_mandatory,
      type: driverLicenseData?.license_type || '',
    } as const

    const profileValue = {
      profile: profilePictureData?.image || '',
      required: profilePictureData?.is_mandatory,
    } as const

    const steps = initialData.filter(
      (data) =>
        data.name !== CONTACT_STEP_NAME &&
        data.name !== PROFILE_PICTURE_STEP_NAME
    )

    const contactStep = {
      data: contactData,
      description: t('auth.register.contact_information_description'),
      dir: contactData?.name,
      document: PersonalInfo,
      label: t('auth.register.contact_information_title'),
      validationSchema: personalInfoSchema,
      value: contactInitialValues,
    }

    const profileStep = {
      data: profilePictureData,
      description: t('auth.register.profile_picture_description'),
      dir: profilePictureData?.name,
      document: ProfilePic,
      label: t('auth.register.profile_picture_title'),
      validationSchema: profilePictureInfoSchema,
      value: profileValue,
    }

    const otherSteps = steps
      .sort((a, b) => a.sort - b.sort)
      .map((step) => {
        const genericSchema = getGenericStepSchema({
          hasExpirationDate: step.has_expiration,
          isRequired: step.is_mandatory,
        })

        const genericStep = {
          data: step,
          description: step.description,
          document: GenericDocs,
          label: step.title,
          validationSchema: genericSchema,
          value: {
            docExpirationDate: step.expiration_date || '',
            document: step.key1,
          },
        }

        if (step.name === DRIVER_STEP_NAME) {
          return {
            ...genericStep,
            document: LicenseDocs,
            validationSchema: licenseInfoSchema,
            value: driverLicenseInitialValues,
          }
        }

        return genericStep
      })

    return [contactStep, ...otherSteps, profileStep]
  }, [initialData, t])

  const steps = getSteps()

  const maxSteps = steps.length

  const getPayloadData = useCallback(
    (step: string, values: FormSchemaValue) => {
      switch (step) {
        case CONTACT_STEP_NAME: {
          const contactValues = values as PersonalInfoSchemaType

          return {
            data: {
              address: contactValues.address,
              birth_date: contactValues.dateOfBirth,
              city_id: contactValues.city,
              country_id: contactValues.country,
              first_name: contactValues.firstName,
              last_name: contactValues.lastName,
              phone: contactValues.phoneNumber?.replace(/[()-]/g, ''),
            },
            name: CONTACT_STEP_NAME,
          }
        }
        case DRIVER_STEP_NAME: {
          const driverValues = values as LicenseInfoSchemaType

          return {
            data: {
              expiration_date: driverValues.docExpirationDate,
              key1: driverValues.front,
              key2: driverValues.back,
              license_number: driverValues.licenseNumber,
              license_type: driverValues.type,
            },
            document_id: steps[activeStep].data?.document_id,
          }
        }
        case PROFILE_PICTURE_STEP_NAME: {
          const profileValues = values as ProfilePictureInfoSchemaType

          return {
            data: {
              key1: profileValues.profile,
            },
            name: PROFILE_PICTURE_STEP_NAME,
          }
        }
        default: {
          const defaultValues = values as GenericInfoSchemaType

          return {
            data: {
              key1: defaultValues.document,
              ...(defaultValues.docExpirationDate
                ? { expiration_date: defaultValues.docExpirationDate }
                : null),
            },
            document_id: steps[activeStep].data?.document_id,
          }
        }
      }
    },
    [activeStep, steps]
  )

  const stepFeed = useCallback(
    async (step: string, values: FormSchemaValue) => {
      try {
        const body = getPayloadData(step, values)
        const response = await updateOnboarding(body)

        if (response?.success === false) {
          throw new Error(
            t('validations.existing_phone') || t('errors.something_wrong')
          )
        }

        return response
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    [
      activeStep,
      getPayloadData,
      initialData,
      updateOnboarding,
    ]
  )

  const skipStep = async () => {
    let skipBody = {}

    if (initialData[activeStep]?.name === PROFILE_PICTURE_STEP_NAME) {
      skipBody = {
        name: initialData[activeStep].name,
      }
    } else {
      skipBody = {
        document_id: initialData[activeStep].document_id,
      }
    }

    await skipOnboardingStep(skipBody)

    if (activeStep === maxSteps - 1) {
      setIsOnboarded(true)
      navigate(routes.portal.home)
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleClose = (_?: Event | SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  const handleSubmit = useCallback(
    async (
      values: FormSchemaValue,
      actions: FormikHelpers<FormSchemaValue>
    ) => {
      try {
        setAlertEvent((prevAlert) => ({ ...prevAlert, open: false }))

        await stepFeed(initialData[activeStep]?.name, values)

        if (initialData[activeStep]?.name === CONTACT_STEP_NAME) {
          await getOnboardData()
        }
        actions.resetForm()
        if (activeStep === maxSteps - 1) {
          setIsOnboarded(true)
          navigate(routes.portal.home)
        } else {
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
      } catch (error) {
        const errorMessage = error
          ? t('validations.existing_phone')
          : t('errors.something_wrong')
        setAlertEvent({
          message: errorMessage,
          open: true,
          type: 'error',
        })
      }
    },
    [
      activeStep,
      initialData,
      getOnboardData,
      maxSteps,
      navigate,
      setIsOnboarded,
      stepFeed,
    ]
  )

  const formInitialValue = useMemo(
    () => steps[activeStep]?.value,
    [activeStep, steps]
  ) as FormSchemaValue

  return (
    <Grid
      container
      justifyContent="center"
      rowGap={2}
      sx={{
        backgroundColor: grey[50],
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Grid container item justifyContent="space-between">
        <Grid item>
          <Button
            color="secondary"
            disableElevation
            onClick={signOut}
            size="small"
            startIcon={<LogoutIcon />}
            variant="contained"
          >
            {t('usermenu.logout')}
          </Button>
        </Grid>
        <Grid item>
          <LanguageSelect />
        </Grid>
      </Grid>
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
        xs={12}
      >
        <Typography
          sx={{ display: 'flex', justifyContent: 'center' }}
          textAlign="center"
          variant="h6"
        >
          {t('auth.register.step_label')}
        </Typography>
        <Typography textAlign="center" variant="subtitle2">
          {activeStep + 1} / {maxSteps}
        </Typography>
        {!steps[activeStep].data?.is_mandatory && (
          <Typography textAlign="center" variant="body1">
            [ {t('auth.register.optional_label')} ]
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography textAlign="center" variant="h3">
          {steps[activeStep].label}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography textAlign="center" variant="subtitle2">
          {steps[activeStep].description}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={formInitialValue}
          onSubmit={handleSubmit}
          validationSchema={steps[activeStep].validationSchema}
        >
          {({ dirty, isValid }) => (
            <Form>
              <Grid container justifyContent="center">
                <Grid item lg={4} xs={12}>
                  <StepDocuments
                    city={city}
                    dir={steps[activeStep].data?.name || ''}
                    document={steps[activeStep].document}
                    hasExpirationDate={!!steps[activeStep].data?.has_expiration}
                    value={steps[activeStep].value as FormSchemaValue}
                  />
                </Grid>
                <Grid item xs={12}>
                  <OnboardStepper
                    activeStep={activeStep}
                    handleBack={handleBack}
                    isNextButtonDisabled={
                      (!steps[activeStep].data?.is_mandatory && !dirty) ||
                      !isValid
                    }
                    maxSteps={maxSteps}
                    required={Boolean(steps[activeStep].data?.is_mandatory)}
                    skipStep={skipStep}
                    submitting={isUpdatingOnboarding || isSkippingOnboarding}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={6}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default Onboard
